import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Make Pusher available globally
window.Pusher = Pusher;

/**
 * Initialize Laravel Echo with authentication
 * @param {string} token - JWT authentication token
 * @returns {Echo} Configured Echo instance
 */
export const initializeEcho = (token) => {
  if (window.Echo) {
    // Already initialized
    return window.Echo;
  }

  // Determine environment (you can adjust this based on your setup)
  const isProduction = process.env.REACT_APP_APP_ENV === 'production';
  const isStaging = process.env.REACT_APP_APP_ENV === 'staging';

  let config = {};

  if (isProduction) {
    // Live/Production
    config = {
      broadcaster: 'reverb',
      key: process.env.REACT_APP_REVERB_KEY || "wcabxofxahtsp064umhm",
      wsHost: process.env.REACT_APP_REVERB_HOST || "reverb.food.ogwugo.xyz",
      wsPort: null,
      wssPort: null,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: process.env.REACT_APP_REVERB_AUTH_ENDPOINT || 'https://server.food.ogwugo.xyz/broadcasting/auth',
    };
  } else if (isStaging) {
    // Staging
    config = {
      broadcaster: 'reverb',
      key: process.env.REACT_APP_REVERB_KEY || "wcabxofxahtsp064umhm",
      wsHost: process.env.REACT_APP_REVERB_HOST || "reverb.food.test.ogwugo.xyz",
      wsPort: null,
      wssPort: null,
      forceTLS: true,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: process.env.REACT_APP_REVERB_AUTH_ENDPOINT || 'https://server.food.test.ogwugo.xyz/broadcasting/auth',
    };
  } else {
    // Local development
    config = {
      broadcaster: 'reverb',
      key: process.env.REACT_APP_REVERB_KEY || "wcabxofxahtsp064umhm",
      wsHost: process.env.REACT_APP_REVERB_HOST || "0.0.0.0",
      wsPort: 8080,
      wssPort: 8080,
      forceTLS: false,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: process.env.REACT_APP_REVERB_AUTH_ENDPOINT || 'http://127.0.0.1:8000/broadcasting/auth',
    };
  }

  // Add authentication
  config.auth = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  window.Echo = new Echo(config);
  return window.Echo;
};

/**
 * Disconnect and cleanup Echo instance
 */
export const disconnectEcho = () => {
  if (window.Echo) {
    window.Echo.disconnect();
    window.Echo = null;
  }
};

/**
 * Subscribe to business order events
 * @param {string} businessId - Business ID to subscribe to
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onOrderCreated - Called when new order is created
 * @param {Function} callbacks.onOrderUpdated - Called when order is updated
 * @returns {Object} Channel instance
 */
export const subscribeToBusinessOrders = (businessId, callbacks = {}) => {
  if (!window.Echo) {
    console.error('Echo not initialized. Call initializeEcho first.');
    return null;
  }

  if (!businessId) {
    console.error('Business ID is required to subscribe to orders.');
    return null;
  }

  const channel = window.Echo.private(`business.${businessId}`);

  if (callbacks.onOrderCreated) {
    channel.listen('BroadcastCreatedOrder', callbacks.onOrderCreated);
  }

  if (callbacks.onOrderUpdated) {
    channel.listen('BroadcastUpdatedOrder', callbacks.onOrderUpdated);
  }

  return channel;
};

/**
 * Unsubscribe from business order events
 * @param {string} businessId - Business ID to unsubscribe from
 */
export const unsubscribeFromBusinessOrders = (businessId) => {
  if (!window.Echo || !businessId) return;

  const channel = window.Echo.private(`business.${businessId}`);
  channel.stopListening('BroadcastCreatedOrder');
  channel.stopListening('BroadcastUpdatedOrder');
  window.Echo.leave(`business.${businessId}`);
};
